import { FEATURES } from "@ovou-inc/ovou-ui";

import { FEATURE_ACTION } from "constants/features";
import { usePermissionsState } from "context";
import useCheckPermission from "hooks/useCheckPermission";
import { USER_IAM_ROLE } from "types/TeamUsersResponse";

// An active owner/admin is on the full plan: they're always entitled to
// configure template features, and the upgrade modal is a no-op for them
// (UpgradeModal returns null for these roles). Everyone else who can reach the
// editor (e.g. legacy/limited owners) falls back to their permission.
const ACTIVE_FULL_ACCESS_ROLES: Array<USER_IAM_ROLE | undefined> = [
  USER_IAM_ROLE.TEAM_ACTIVE_OWNER,
  USER_IAM_ROLE.TEAM_ACTIVE_ADMIN,
];

/**
 * Whether the current team can configure a plan-gated template cluster feature.
 *
 * `useCheckPermission` reads the user's EFFECTIVE (merged) permission from
 * `auth/role`, which can be locked below the team's plan tier by member-lock /
 * injection overrides — but the backend template gate is TIER-only. To avoid
 * wrongly locking an entitled team (and showing an upgrade affordance that
 * can't open), an active owner/admin is always treated as entitled.
 */
export const useCanConfigureTemplateFeature = (feature: FEATURES): boolean => {
  const canEdit = useCheckPermission(FEATURE_ACTION.EDIT, feature);
  const { permission } = usePermissionsState();
  const isActiveOwnerOrAdmin = ACTIVE_FULL_ACCESS_ROLES.includes(
    permission?.user_iam_role,
  );
  return canEdit || isActiveOwnerOrAdmin;
};
